import { ControllerProps, FieldValues } from "react-hook-form";
import { SelectionOption } from "../../../types";
import {
  ItecToggleButtonGroup,
  ItecToggleButtonGroupProps,
} from "./toggle-button-group";
import { useField } from "../../../hooks";
import { FormControl, FormHelperText, FormLabel } from "@mui/material";

export type ControlledToggleButtonGroupProps<
  TOption extends SelectionOption,
  TForm extends FieldValues
> = Required<Pick<ControllerProps<TForm>, "control" | "name">> & {
  /**
   * TODO: async options
   */
  /**
   * At least one value must be selected at all times.
   */
  enforceValueSet?: boolean;
  helperText?: string;
  label?: string;
} & Omit<ItecToggleButtonGroupProps<TOption>, "onChange" | "value">;

export function ControlledToggleButtonGroup<
  TOption extends SelectionOption,
  TForm extends FieldValues
>(props: ControlledToggleButtonGroupProps<TOption, TForm>) {
  const {
    name,
    control,
    label,
    helperText,
    enforceValueSet,
    options,
    ...rest
  } = props;

  const { field, fieldState, formState } = useField<TOption, TForm>({
    name,
    control,
  });

  const handleChange = (value: number | null) => {
    if (!value && enforceValueSet) return;

    if (value === field.value?.value) {
      field.onChange(undefined);
    } else {
      field.onChange(options.find((v) => v.value === value));
    }
  };

  return (
    <FormControl error={!!fieldState.error}>
      {label && <FormLabel>{label}</FormLabel>}
      <ItecToggleButtonGroup
        value={field.value?.value}
        onChange={handleChange}
        options={options}
        {...rest}
      />
      <FormHelperText>
        {fieldState.error?.message || (helperText && helperText)}
      </FormHelperText>
    </FormControl>
  );
}
